import { Component, computed, effect, inject, Input, input, InputSignal } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { MatDialogRef } from "@angular/material/dialog";
import { checkDuplicateName } from "@bitwarden/web-vault/app/shared/utils/helper-new-view-name";
import { CategoryUtils } from "@bitwarden/web-vault/app/models/view/category/category.utils";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { findCategoryById } from "@bitwarden/web-vault/app/shared/utils/helper-categories";

@Component({
  selector: "app-categories-add-edit",
  templateUrl: "./categories-add-edit.component.html",
  styles: [".mat-form-field {width: 100%;}"],
})
export class CategoriesAddEditComponent {
  form: FormGroup;
  loading = false;
  title: string;
  category: InputSignal<CategoryView> = input(null);
  originalCategoryName: string = "";
  @Input() actionSucceeded: CallableFunction;

  private i18nService: I18nService = inject(I18nService);
  private userStore: UserStoreService = inject(UserStoreService);
  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private categoryUtils: CategoryUtils;

  protected readonly saveButtonOptions = new ActionButton({
    text: this.i18nService.t("save"),
    isEnabled: true,
    type: "submit",
    onClick: this.submit.bind(this),
    fullClass: "btn-primary-teal",
  });

  constructor(
    private dialogRef: MatDialogRef<CategoriesAddEditComponent>,
    private globalService: GlobalService,
    private formBuilder: FormBuilder,
  ) {
    this.initializeForm();
    this.categoryUtils = new CategoryUtils();
    effect(() => {
      if (this.category()) {
        this.originalCategoryName = this.category().name;
        this.updateCategoryName(this.originalCategoryName);
      }
    });
  }

  editMode = computed(() => this.category() != null);

  initializeForm() {
    this.form = this.formBuilder.group({
      categoryName: ["", Validators.required],
    });
  }

  async submit() {
    try {
      if (this.form.valid) {
        if (this.editMode()) {
          await this.updateCategory();
        } else {
          await this.createCategory();
        }
      } else {
        this.saveButtonOptions.enableButton(true);
        this.globalService.showWarningMessage("warning", "categoryNameInfo");
      }
    } catch (e) {
      this.globalService.showErrorMessage("errorOccurred", "somethingWentWrong");
    }
  }

  hasErrorInCategoryUitl(error: Error) {
    this.notifyService.notify(NotificationEnum.error, "errorOccurred", error.message, true);
    this.updateCategoryName(this.originalCategoryName);
    this.saveButtonOptions.enableButton(true);
  }

  async createCategory() {
    try {
      const newCategoryName = this.form.controls.categoryName.value;
      await this.categoryUtils.addNewCategory(newCategoryName, this.userStore);
      this.successfulMessage(newCategoryName);
    } catch (e) {
      this.hasErrorInCategoryUitl(e);
    }
  }

  updateCategoryName(categoryName: string) {
    this.form.patchValue({
      categoryName: categoryName,
    });
  }

  checkEditNameIsDuplicate() {
    return this.form.controls.categoryName.value === this.originalCategoryName;
  }

  async updateCategory() {
    if (this.checkEditNameIsDuplicate()) {
      this.successfulMessage(this.originalCategoryName);
      return;
    }

    const categoriesByUser = this.userStore.categories.categoryViewsSorted();
    try {
      const categoryToBeUpdated = findCategoryById(
        categoriesByUser,
        this.category().id,
      ).clone() as CategoryView;
      categoryToBeUpdated.name = this.form.controls.categoryName.value;

      const updatedCategory = await this.categoryUtils.updateCategoryName(
        categoryToBeUpdated.name,
        categoryToBeUpdated as CategoryView,
        this.userStore,
      );
      if (updatedCategory instanceof CategoryView) {
        this.successfulMessage(updatedCategory.name);
      }
    } catch (e) {
      this.hasErrorInCategoryUitl(e);
    }
  }

  successfulMessage(newCategoryName: string) {
    const successfulResponse = {
      newCategoryText: newCategoryName,
      successfulText: this.editMode() ? "updatedSuccessfully" : "createdSuccessfully",
    };
    this.actionSucceeded(successfulResponse);
    this.dialogRef.close();
  }

  // async deleteCategory(category: Category) {
  //   // TODO check if it is a callable
  //   this.delete(category);
  // }

  onCategoryNameChange(categoryName: string) {
    this.form.controls.categoryName.setValue(categoryName);
  }
}
